// src/components/admin/ImageUploader.js
// Reusable image upload widget for admin screens.
// Props:
//   bucket     — Supabase Storage bucket name ('products' | 'slides')
//   folder     — subfolder inside bucket (e.g. 'product-images', 'hero')
//   value      — current image URL string
//   onChange   — called with the new public URL after upload
//   onError    — called with error message string
//   label      — optional button label override
//   preview    — 'banner' (wide 16:9) | 'square' (1:1) | 'none'  default: 'square'

import React, { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';

const ACCEPTED = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_MB = 8;

export default function ImageUploader({
  bucket,
  folder = '',
  value = '',
  onChange,
  onError,
  label = 'Upload Image',
  preview = 'square',
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // 0-100 cosmetic
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset so same file can be re-selected after removal
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;

    // Client-side validation
    if (!file.type.startsWith('image/')) {
      onError?.('Please select an image file (JPEG, PNG, WebP, GIF).');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      onError?.(`Image must be under ${MAX_MB} MB. Yours is ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
      return;
    }

    setUploading(true);
    setProgress(10);

    // Build a unique path: folder/timestamp-random.ext
    const ext = file.name.split('.').pop().toLowerCase() || 'jpg';
    const name = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const path = folder ? `${folder}/${name}` : name;

    setProgress(30);

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, file, { upsert: false, contentType: file.type });

    if (uploadError) {
      setUploading(false);
      setProgress(0);
      // Friendly bucket-not-found message
      const msg = uploadError.message?.toLowerCase().includes('bucket')
        ? `Storage bucket "${bucket}" not found. Create it in Supabase Dashboard → Storage → New bucket → name: "${bucket}", public: true.`
        : `Upload failed: ${uploadError.message}`;
      onError?.(msg);
      return;
    }

    setProgress(80);

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    setProgress(100);
    setUploading(false);
    onChange?.(urlData.publicUrl);
  };

  const handleRemove = () => {
    onChange?.('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const aspectStyle = preview === 'banner'
    ? { width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 10, marginBottom: 10, border: '1px solid rgba(255,255,255,.07)', display: 'block' }
    : preview === 'square'
    ? { width: 80, height: 80, objectFit: 'cover', borderRadius: 10, marginBottom: 10, border: '1px solid rgba(255,255,255,.07)', display: 'block' }
    : null;

  const placeholderStyle = preview === 'banner'
    ? { width: '100%', height: 80, background: '#1a1a1a', borderRadius: 10, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: '1.4rem', border: '1px dashed rgba(255,255,255,.1)' }
    : preview === 'square'
    ? { width: 80, height: 80, background: '#1a1a1a', borderRadius: 10, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: '1.4rem', border: '1px dashed rgba(255,255,255,.1)' }
    : null;

  return (
    <div className="image-uploader">
      {/* Preview or placeholder */}
      {preview !== 'none' && (
        value
          ? <img src={value} alt="preview" style={aspectStyle} />
          : <div style={placeholderStyle}>🖼️</div>
      )}

      {/* Upload progress bar */}
      {uploading && (
        <div style={{ height: 3, background: '#1a1a1a', borderRadius: 2, marginBottom: 10, overflow: 'hidden' }}>
          <div style={{
            height: '100%', background: '#4cca74', borderRadius: 2,
            width: `${progress}%`, transition: 'width .3s ease',
          }} />
        </div>
      )}

      {/* Controls row */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Upload button — styled label wrapping hidden input */}
        <label className="upload-btn" style={{ opacity: uploading ? .6 : 1, pointerEvents: uploading ? 'none' : 'auto' }}>
          {uploading ? `Uploading ${progress}%…` : `📁 ${label}`}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            onChange={handleFile}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>

        {/* Remove button */}
        {value && !uploading && (
          <button type="button" className="upload-btn-remove" onClick={handleRemove}>
            ✕ Remove
          </button>
        )}
      </div>

      {/* URL paste fallback */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: '.7rem', color: '#444', marginBottom: 4, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Or paste a URL
        </div>
        <input
          className="input-field"
          type="url"
          value={value}
          onChange={e => onChange?.(e.target.value)}
          placeholder="https://…/image.jpg"
          style={{ fontSize: '.82rem' }}
        />
      </div>
    </div>
  );
}
